import React, { useCallback, useMemo, useContext } from 'react';
import _ from 'lodash';
import CommonLayout from 'views/App/Portal/_layouts/CommonLayout';
import { translate as t } from 'hooks/useTranslations';
import GridSection from 'components/_layouts/GridSection';
import { useTemplates } from '../../_shared/_data_sources/useTemplates';
import { Box, Button, Typography, CircularProgress, Stack } from '@mui/material';
import PageToolbar from '_lex/_atoms/PageToolbar';
import { useNavigate } from 'react-router-dom';
import { AppContext } from 'views/App/context';
import { QuickSearch, ActionMenu } from 'components/_atoms';
import useUrlParams from 'hooks/useUrlParams';
import { TemplateStatus } from '../../_shared/_types';
import datetimeHelper from 'helpers/datetimeHelper';
import TemplateFiltersGroup from '../../_shared/_components/TemplateFiltersGroup';

export type TemplateListParams = {
  search?: string | null;
  status?: TemplateStatus | null;
  effectiveDateFrom?: string | null;
  effectiveDateTo?: string | null;
  lastUpdatedFrom?: string | null;
  lastUpdatedTo?: string | null;
  authors?: string[] | null;
};

const defaultParams: TemplateListParams = {
  search: null,
  status: null,
  effectiveDateFrom: null,
  effectiveDateTo: null,
  lastUpdatedFrom: null,
  lastUpdatedTo: null,
  authors: null,
};

const LexipolTemplateList: React.FC = () => {
  const navigate = useNavigate();
  const { hasPermission, currentOrganizationId } = useContext(AppContext);
  const [params, setParams] = useUrlParams(defaultParams);

  const updateParams = useCallback(
    (updatedParams: TemplateListParams) => {
      setParams((prev: TemplateListParams) => ({
        ...prev,
        ...updatedParams,
      }));
      document.body.dispatchEvent(new Event('params-changed'));
    },
    [setParams]
  );

  const clearParams = useCallback(() => {
    setParams(defaultParams);
    document.body.dispatchEvent(new Event('params-changed'));
  }, [setParams]);

  const queryParams = useMemo(() => {
    const cleanParams = _.omitBy({ ...params }, _.isEmpty);
    return cleanParams;
  }, [params]);

  const isFiltered = !_.isEmpty(queryParams);

  const templatesQuery = useTemplates(queryParams);
  const allTemplates = templatesQuery.data?.pages.flatMap((page) => page.content) || [];
  const totalCount = templatesQuery.data?.pages[0]?.count || 0;
  const { isLoading, isError, error } = templatesQuery;

  const canManageTemplates = hasPermission('manageFieldBank', currentOrganizationId || '');

  const onSearchChange = useCallback(
    (search: string) => {
      updateParams({ search: search || null });
    },
    [updateParams]
  );

  const formatDate = (dateString?: string) => {
    return datetimeHelper.formatDate(dateString, { format: 'short', missingDisplay: '-' });
  };

  const getActionMenuItems = useCallback(
    (templateId: number) => [
      {
        key: 'details',
        label: t('performanceReporting.templates.list.actions.templateDetails'),
        onClick: () => {
          navigate(`./details/${templateId}`);
        },
      },
      ...(canManageTemplates
        ? [
            {
              key: 'edit',
              label: t('performanceReporting.templates.list.actions.edit'),
              onClick: () => navigate(`./builder/${templateId}`),
            },
          ]
        : []),
    ],
    [canManageTemplates, navigate]
  );

  const columns = useMemo(
    () => ({
      name: {
        header: t('performanceReporting.templates.list.columns.name'),
        flex: 2,
        renderer: (params: any) => (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Typography
              variant='body2'
              fontWeight='medium'
            >
              {params.row.name}
            </Typography>
            <Typography
              variant='caption'
              color='text.secondary'
            >
              {params.row.description}
            </Typography>
          </Box>
        ),
      },
      createdBy: {
        header: t('performanceReporting.templates.list.columns.author'),
        flex: 1.5,
        renderer: (params: any) => <Typography variant='body2'>{params.row.createdBy || '-'}</Typography>,
      },
      effectiveDate: {
        header: t('performanceReporting.templates.list.columns.effectiveDate'),
        flex: 1,
        renderer: (params: any) => <Typography variant='body2'>{formatDate(params.row.effectiveDate)}</Typography>,
      },
      publishedDate: {
        header: t('performanceReporting.templates.list.columns.publishedDate'),
        flex: 1,
        renderer: (params: any) => <Typography variant='body2'>{formatDate(params.row.lastPublishDate)}</Typography>,
      },
      actions: {
        header: '',
        flex: 0.5,
        renderer: (params: any) => (
          <ActionMenu
            id={`template-actions-${params.row.id}`}
            items={getActionMenuItems(params.row.id)}
          />
        ),
      },
    }),
    [getActionMenuItems, formatDate]
  );

  return (
    <CommonLayout
      pageTitle={t('performanceReporting.templates.list.title')}
      headerProps={{
        title: t('performanceReporting.templates.list.title'),
        subtitle: t('performanceReporting.templates.lexipol.list.subtitle'),
      }}
    >
      <Box sx={{ display: 'grid', gridTemplateRows: 'auto auto 1fr', gap: 4, minHeight: 0 }}>
        <PageToolbar
          leftItems={
            <Stack
              direction='row'
              spacing={3}
              sx={{
                alignItems: 'center',
                '& > *': {
                  minWidth: 'fit-content',
                  flexShrink: 0,
                },
              }}
            >
              <QuickSearch
                id='templates_search'
                initialValue={params.search || ''}
                onChange={onSearchChange}
                placeholder={t('performanceReporting.templates.list.search.placeholder')}
              />
            </Stack>
          }
          rightItems={
            <Stack
              direction='row'
              spacing={2}
              sx={{ alignItems: 'center', display: 'flex' }}
            >
              {canManageTemplates && (
                <Button
                  variant='contained'
                  color='primary'
                  onClick={() => navigate('./new')}
                >
                  {t('performanceReporting.templates.list.createTemplate')}
                </Button>
              )}
            </Stack>
          }
        />
        <TemplateFiltersGroup
          params={params}
          updateParams={updateParams}
          clearParams={clearParams}
        />
        {isLoading ? (
          <Box
            sx={{
              p: 4,
              textAlign: 'center',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <GridSection
            columns={columns}
            data={allTemplates}
            totalCount={totalCount}
            query={templatesQuery}
            gridKey={JSON.stringify(queryParams)}
            gridProps={{
              getRowId: (row: any) => row.id,
            }}
          />
        )}
      </Box>
    </CommonLayout>
  );
};

export default LexipolTemplateList;

